import {
  LLMUnavailableError,
  type LLMClient,
  type TextGenerationRequest,
  type TextGenerationResponse,
  type ImageGenerationRequest,
  type ImageGenerationResponse,
} from "./LLMClient.js";

/** Default model for text generation — fast and economical for background gen. */
const DEFAULT_TEXT_MODEL = "claude-haiku-4-5-20251001";

/**
 * Anthropic Claude client for user-supplied API keys (BYOK).
 *
 * Calls the Anthropic Messages API directly from the browser using the
 * `anthropic-dangerous-direct-browser-access` header, which Anthropic
 * explicitly supports for BYOK browser applications.
 *
 * Image generation is not supported by Claude; calling generateImage() will
 * throw LLMUnavailableError until L04 wires in a separate image provider.
 */
export class ClaudeClient implements LLMClient {
  readonly isConfigured = true;

  constructor(private readonly apiKey: string) {}

  async generateText(req: TextGenerationRequest): Promise<TextGenerationResponse> {
    const body: Record<string, unknown> = {
      model: DEFAULT_TEXT_MODEL,
      max_tokens: req.maxTokens ?? 1024,
      messages: [{ role: "user", content: req.userPrompt }],
    };
    if (req.systemPrompt) body["system"] = req.systemPrompt;

    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "x-api-key": this.apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      throw new LLMUnavailableError(
        `Anthropic API error ${response.status}${detail ? `: ${detail}` : ""}`,
      );
    }

    const data = (await response.json()) as {
      content: Array<{ type: string; text: string }>;
      model: string;
    };

    const textBlock = data.content.find((b) => b.type === "text");
    if (!textBlock) throw new LLMUnavailableError("No text block in Anthropic response");

    return { text: textBlock.text, modelId: data.model };
  }

  generateImage(_req: ImageGenerationRequest): Promise<ImageGenerationResponse> {
    return Promise.reject(
      new LLMUnavailableError(
        "Image generation requires a separate provider (wired in L04)",
      ),
    );
  }
}
